import { SideNavigation } from '@cinesend/ui-kit'
import {
  AirTrafficControl,
  BookBookmark,
  BookOpen,
  Buildings,
  FilmSlate,
  FilmStrip,
  Folder,
  FolderSimpleLock,
  HardHat,
  Image,
  List,
  Megaphone,
  Microphone,
  MusicNote,
  MusicNotes,
  Pulse,
  RocketLaunch,
  QuestionMark,
  Rows,
  SidebarSimple,
  SquaresFour,
  Stack,
  Star,
  Television,
  UserCircle,
  UsersThree,
  VideoCamera,
  Warning,
  Waveform,
  Wrench,
} from '@phosphor-icons/react'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import AccountMenu from './account_menu'
import AirlineCycleDropdowns from './airline_cycle_dropdowns'
import SettingsBackButton from './settings_back_button'
import useUtilities from '../../hooks/use_utilities'
import { useQuery } from '@tanstack/react-query'
import { useRef } from 'react'

function Sidebar({ user, logOut }) {
  const navigate = useNavigate()
  const { assetTypes } = useUtilities()
  const { airlineID, cycleID } = useParams()
  const lastAirlineID = useRef(airlineID)
  if (airlineID) lastAirlineID.current = airlineID

  const fullPathname = useLocation().pathname

  const root = `/airlines/${airlineID}/cycles/${cycleID}`
  let pathname = fullPathname.replace(root, '')

  const { data: cycle } = useQuery(['cycles/', cycleID], { enabled: Boolean(cycleID) })
  // Keep airline queries subscribed while account settings hide AccountMenu.
  useQuery(['airlines'])
  useQuery(['airlines/', lastAirlineID.current], { enabled: Boolean(lastAirlineID.current) })
  const availableAssetTypes = cycle
    ? assetTypes
        ?.filter((assetType) => cycle?.airline?.asset_types?.includes(assetType.value))
        .map((assetType) => ({
          value: assetType.value,
          text: assetType.plural_label,
          to: `/metadata/${assetType.route_value}`,
          route: `${root}/metadata/${assetType.route_value}`,
        }))
    : []

  const viewAccount = fullPathname.startsWith('/settings/account')

  const icon = (Icon) => <Icon size={20} weight='regular' aria-hidden='true' />

  const isActive = (to) => pathname === to || pathname.startsWith(`${to}/`)

  const toItem = (link) => ({
    id: link.route,
    label: link.text,
    icon: link.icon,
    active: isActive(link.to),
    onClick: () => navigate(link.route),
  })

  const assetTypeIcons = {
    movie: FilmStrip,
    series: Television,
    music: MusicNotes,
    audiobook: BookOpen,
    podcast: Microphone,
    ad: Megaphone,
    'featured-content': Star,
    episode: VideoCamera,
    track: MusicNote,
    'podcast-track': Waveform,
    'audiobook-track': BookBookmark,
  }

  const assetTypeItems = (availableAssetTypes ?? []).map((assetType) =>
    toItem({
      text: assetType.text,
      to: assetType.to,
      route: assetType.route,
      icon: icon(assetTypeIcons[assetType.value] ?? QuestionMark),
    })
  )

  const cycleSections = !cycleID
    ? []
    : [
        {
          id: 'cycle',
          label: 'Cycle',
          items: [
            { text: 'Dashboard', to: '/dashboard', route: `${root}/dashboard`, icon: icon(AirTrafficControl) },
            { text: 'Setup', to: '/details', route: `${root}/details`, icon: icon(Wrench) },
            { text: 'Issues', to: '/issues', route: `${root}/issues`, icon: icon(Warning) },
            { text: 'Submission', to: '/submission', route: `${root}/submission`, icon: icon(RocketLaunch) },
          ].map(toItem),
        },
        {
          id: 'catalog',
          label: 'Catalog',
          items: [
            { text: 'Sections', to: '/sections', route: `${root}/sections`, icon: icon(SquaresFour) },
            { text: 'Categories', to: '/categories', route: `${root}/categories`, icon: icon(Rows) },
          ]
            .map(toItem)
            .concat(assetTypeItems),
        },
        {
          id: 'media',
          label: 'Media',
          items: [
            { text: 'Images', to: '/images', route: `${root}/images`, icon: icon(Image) },
            { text: 'Files', to: '/files', route: `${root}/files`, icon: icon(Folder) },
          ].map(toItem),
        },
        {
          id: 'preroll',
          label: 'Advertising & Pre-roll',
          items: [
            { text: 'Clips', to: '/ads/clips', route: `${root}/ads/clips`, icon: icon(Megaphone) },
            { text: 'Ad Sets', to: '/ads/ad-sets', route: `${root}/ads/ad-sets`, icon: icon(Stack) },
            { text: 'Slate Sets', to: '/ads/slate-sets', route: `${root}/ads/slate-sets`, icon: icon(FilmSlate) },
          ].map(toItem),
        },
        {
          id: 'configuration',
          label: 'Configuration',
          items: [{ text: 'Lists', to: '/lists', route: `${root}/lists`, icon: icon(List) }].map(toItem),
        },
      ]

  const accountSections = [
    {
      id: 'account',
      items: [
        {
          text: 'Profile',
          to: '/settings/account/profile',
          route: '/settings/account/profile',
          icon: icon(UserCircle),
        },
        {
          text: 'Security',
          to: '/settings/account/security',
          route: '/settings/account/security',
          icon: icon(FolderSimpleLock),
        },
        {
          text: 'Activity Logs',
          to: '/settings/account/activity-logs',
          route: '/settings/account/activity-logs',
          icon: icon(Pulse),
        },
      ].map((link) => ({
        ...toItem(link),
        active:
          link.to === '/settings/account/profile'
            ? fullPathname === '/settings/account' || fullPathname.startsWith('/settings/account/profile')
            : isActive(link.to),
      })),
    },
    {
      id: 'organization',
      label: user.organization?.name || 'Organization',
      items: [
        {
          text: 'Organization',
          to: '/settings/account/organization',
          route: '/settings/account/organization',
          icon: icon(Buildings),
        },
        {
          text: 'Members',
          to: '/settings/account/members',
          route: '/settings/account/members',
          icon: icon(UsersThree),
        },
      ].map(toItem),
    },
  ]

  const accountItems = [
    user.has_unrestricted_edit_role && {
      id: 'administration',
      label: 'Administration',
      icon: icon(HardHat),
      onClick: () => {
        localStorage.setItem('backToMetadata', fullPathname)
        navigate('/settings/airlines')
      },
    },
  ].filter(Boolean)

  return (
    <div className='h-full'>
      <SideNavigation
        organization={viewAccount ? null : <AccountMenu user={user} logOut={logOut} />}
        header={viewAccount ? <SettingsBackButton /> : null}
        workspace={viewAccount ? null : <AirlineCycleDropdowns />}
        sections={viewAccount ? accountSections : cycleSections}
        footerItems={viewAccount ? [] : accountItems}
        collapsed={viewAccount ? false : undefined}
        collapseIcon={viewAccount ? undefined : <SidebarSimple size={20} weight='regular' aria-hidden='true' />}
      />
    </div>
  )
}

export default Sidebar
